import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { DraftCard } from "../components/DraftCard";
import { EmptyState } from "../components/EmptyState";
import { Skeleton, SkeletonList } from "../components/ui/Skeleton";
import { useInbox } from "../hooks/useInbox";

const Home = () => {
  const drafts = useQuery({ queryKey: ["drafts"], queryFn: () => api.drafts.list() });
  const scheduled = useQuery({ queryKey: ["scheduled"], queryFn: () => api.posts.scheduled() });
  const published = useQuery({ queryKey: ["published"], queryFn: () => api.posts.published() });
  const inbox = useInbox();

  const inboxPending = inbox.data?.items.reduce((acc, g) => acc + g.engagements.length, 0) ?? 0;

  const anyLoading = drafts.isLoading || scheduled.isLoading || published.isLoading || inbox.isLoading;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          Visão geral dos seus drafts e posts.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {anyLoading ? (
          <>
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 space-y-3"
              >
                <Skeleton className="h-8 w-12" />
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </>
        ) : (
          <>
            <StatCard label="Drafts pendentes" value={drafts.data?.length ?? 0} link="/drafts" />
            <StatCard label="Agendados" value={scheduled.data?.length ?? 0} link="/scheduled" />
            <StatCard label="Publicados" value={published.data?.length ?? 0} link="/published" />
            <StatCard
              label="Inbox pendentes"
              value={inboxPending}
              link="/inbox"
              highlight={inboxPending > 0}
            />
          </>
        )}
      </div>

      <section>
        <h2 className="text-lg font-semibold mb-3">Últimos drafts</h2>
        {drafts.isLoading && <SkeletonList count={4} />}
        {!drafts.isLoading && (!drafts.data || drafts.data.length === 0) && (
          <EmptyState
            title="Nenhum draft pendente"
            message="Próximo é gerado terça ou quinta às 8h BR."
          />
        )}
        {drafts.data && drafts.data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {drafts.data.slice(0, 4).map((d) => (
              <DraftCard key={d.id} draft={d} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;

const StatCard = ({
  label,
  value,
  link,
  highlight = false,
}: {
  label: string;
  value: number;
  link: string;
  highlight?: boolean;
}) => {
  return (
    <Link
      to={link}
      className={`block rounded-xl border p-4 hover:border-zinc-400 dark:hover:border-zinc-600 transition-colors focus-ring ${
        highlight
          ? "border-amber-300 bg-amber-50 dark:border-amber-700/50 dark:bg-amber-900/20"
          : "border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900"
      }`}
    >
      <div
        className={`text-2xl font-semibold ${
          highlight ? "text-amber-700 dark:text-amber-400" : "text-zinc-900 dark:text-zinc-100"
        }`}
      >
        {value}
      </div>
      <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{label}</div>
    </Link>
  );
};
